<!--
  @component LogsScreen — the project's activity stream.
  tier:      screen (per-project — `/projects/:id/logs`)
  reads:     useIdentityIO       → current project
             useProvenanceIO     → the fused append-only event ledger
  scope:     a readable record of what's happened to this deployment —
             deploys, provisioning, domain + DNS changes. Read-only.
-->
<script lang="ts">
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useProvenanceIO } from "../../../../global/io/nodes/deployment/ProvenanceIO.svelte";
  import Card from "../../../../global/visuals/primitives/Card.svelte";
  import StatusNote from "../../../../global/visuals/primitives/StatusNote.svelte";
  import LogStream from "../../../../global/visuals/primitives/LogStream.svelte";

  const identity = useIdentityIO();
  const provenance = useProvenanceIO();

  const project = $derived(identity.currentProject);
  let liveTail = $state(true);
</script>

{#if !project}
  <div class="notfound">
    <h1>Project not found</h1>
    <p>The logs need a project context. Go back to <a href="#/projects">your projects</a>.</p>
  </div>
{:else}
  <div class="screen">
    <header class="head">
      <div class="title">
        <h1>Logs</h1>
        <span class="live" data-on={liveTail}><span class="live-dot"></span>{liveTail ? "Live" : "Paused"}</span>
      </div>
      <p class="sub">
        {provenance.count} event{provenance.count === 1 ? "" : "s"} this session
      </p>
    </header>

    {#if provenance.count === 0}
      <StatusNote tone="pending" message="Activity shows up here as you deploy, provision resources, and manage domains." />
    {/if}

    <Card eyebrow="Activity" title="Event stream">
      <LogStream
        events={provenance.events}
        liveTail={liveTail}
        onLiveTailChange={(next) => (liveTail = next)}
        emptyLabel="No events yet — deploy or change a resource to see activity here."
      />
    </Card>
  </div>
{/if}

<style>
  .screen { display: flex; flex-direction: column; gap: var(--space-5); max-width: 1000px; font-family: var(--type-font); color: var(--text-primary); }
  .head { display: flex; flex-direction: column; gap: var(--space-2); }
  .title { display: flex; align-items: center; gap: var(--space-3); }
  h1 { margin: 0; font-size: 24px; font-weight: 600; letter-spacing: -0.025em; color: var(--text-primary); }
  .live { display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; color: var(--text-tertiary); padding: 3px var(--space-2); border: 0.5px solid var(--border-subtle); border-radius: var(--radius-pill); }
  .live-dot { width: 6px; height: 6px; border-radius: var(--radius-pill); background: var(--text-ghost); }
  .live[data-on="true"] { color: var(--success); }
  .live[data-on="true"] .live-dot { background: var(--success); }
  .sub { margin: 0; font-size: 13px; color: var(--text-tertiary); }

  .notfound { max-width: 480px; margin: var(--space-8) auto; padding: var(--space-6); background: var(--glass-fill); border: 0.5px solid var(--border); border-radius: var(--radius-lg); text-align: center; font-family: var(--type-font); }
  .notfound h1 { margin: 0 0 var(--space-3); font-size: 18px; font-weight: 600; }
  .notfound p { margin: 0; font-size: 13px; color: var(--text-secondary); }
  .notfound a { color: var(--text-primary); }
</style>
